import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || '';

const styles = {
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '25px',
    flexWrap: 'wrap'
  },
  tab: {
    padding: '12px 24px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  tabActive: {
    borderColor: '#722F37',
    background: '#722F37',
    color: 'white'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '25px'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '20px',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px'
  },
  statLabel: {
    color: '#666'
  },
  statValue: {
    fontWeight: '600',
    color: '#333'
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none'
  },
  primaryButton: {
    background: '#722F37',
    color: 'white'
  },
  successButton: {
    background: '#28a745',
    color: 'white'
  },
  dangerButton: {
    background: '#dc3545',
    color: 'white'
  },
  secondaryButton: {
    background: '#6c757d',
    color: 'white'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '12px 10px',
    borderBottom: '2px solid #f0f0f0',
    fontSize: '13px',
    fontWeight: '600',
    color: '#666'
  },
  td: {
    padding: '12px 10px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px'
  },
  logEntry: {
    padding: '10px 15px',
    borderRadius: '6px',
    marginBottom: '8px',
    fontSize: '13px',
    fontFamily: 'monospace'
  },
  logInfo: {
    background: '#e7f5ff',
    borderLeft: '3px solid #339af0'
  },
  logWarn: {
    background: '#fff9db',
    borderLeft: '3px solid #fcc419'
  },
  logError: {
    background: '#ffe3e3',
    borderLeft: '3px solid #fa5252'
  },
  statusIndicator: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginRight: '8px'
  },
  statusOnline: {
    background: '#28a745'
  },
  statusOffline: {
    background: '#dc3545'
  },
  codeBlock: {
    background: '#1e1e1e',
    color: '#d4d4d4',
    padding: '20px',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '500px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#888'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500'
  }
};

function AdminSystem() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('status');
  const [stats, setStats] = useState(null);
  const [backups, setBackups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [database, setDatabase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const role = localStorage.getItem('adminRole');
    if (role !== 'superadmin') {
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, backupsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/superadmin/system/stats`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/superadmin/backups`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/superadmin/system/logs?limit=50`, { headers: getAuthHeader() })
      ]);

      setStats(await statsRes.json());
      setBackups(await backupsRes.json());
      setLogs(await logsRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDatabase = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/system/database`, { headers: getAuthHeader() });
      setDatabase(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await fetch(`${API_URL}/api/superadmin/backups`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      fetchData();
      alert('Backup creado correctamente');
    } catch (err) {
      alert('Error al crear backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = (filename) => {
    const token = localStorage.getItem('adminToken');
    window.open(`${API_URL}/api/superadmin/backups/download/${filename}?token=${token}`, '_blank');
  };

  const handleRestoreBackup = async (filename) => {
    if (!window.confirm(`¿Restaurar backup ${filename}? Se creará un backup automático antes de restaurar.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/superadmin/backups/restore/${filename}`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (res.ok) {
        alert('Backup restaurado correctamente. La página se recargará.');
        window.location.reload();
      } else {
        const data = await res.json();
        alert('Error: ' + data.error);
      }
    } catch (err) {
      alert('Error al restaurar backup');
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`¿Eliminar backup ${filename}?`)) return;

    try {
      await fetch(`${API_URL}/api/superadmin/backups/${filename}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      fetchData();
    } catch (err) {
      alert('Error al eliminar backup');
    }
  };

  const handleExportFull = () => {
    const token = localStorage.getItem('adminToken');
    window.open(`${API_URL}/api/superadmin/export/full?token=${token}`, '_blank');
  };

  const handleResetSurvey = async () => {
    const confirmText = 'REINICIAR';
    const userInput = window.prompt(
      `⚠️ ATENCIÓN: Esta acción eliminará TODOS los datos de la encuesta activa:\n\n` +
      `• Todas las respuestas\n` +
      `• Todas las notificaciones enviadas\n` +
      `• Los tokens de empleados se resetearán\n\n` +
      `Se creará un backup automático antes de eliminar.\n\n` +
      `Escribe "${confirmText}" para confirmar:`
    );

    if (userInput !== confirmText) {
      if (userInput !== null) {
        setMessage({ type: 'error', text: 'Texto de confirmación incorrecto. Operación cancelada.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
      return;
    }

    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/reset-survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({
          type: 'success',
          text: `Encuesta reiniciada. Eliminados: ${data.deleted.responses} respuestas, ${data.deleted.notifications} notificaciones. Backup creado.`
        });
        fetchData();
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    } finally {
      setResetting(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('es-ES');
  };

  if (loading) {
    return (
      <AdminLayout title="Sistema">
        <div style={styles.emptyState}>Cargando...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Sistema">
      {message.text && (
        <div style={{
          padding: '15px 20px',
          borderRadius: '8px',
          marginBottom: '20px',
          background: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'status' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('status')}
        >
          Estado del Sistema
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'backups' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('backups')}
        >
          Copias de Seguridad
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'logs' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('logs')}
        >
          Logs
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'database' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('database'); fetchDatabase(); }}
        >
          Base de Datos
        </button>
      </div>

      {activeTab === 'status' && stats && (
        <>
          <div style={styles.grid}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                <span style={{ ...styles.statusIndicator, ...styles.statusOnline }}></span>
                Servidor
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Estado</span>
                <span style={{ ...styles.statValue, color: '#28a745' }}>Online</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Tiempo activo</span>
                <span style={styles.statValue}>{stats.server.uptimeFormatted}</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Node.js</span>
                <span style={styles.statValue}>{stats.server.nodeVersion}</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Memoria</span>
                <span style={styles.statValue}>{stats.server.memoryFormatted}</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Plataforma</span>
                <span style={styles.statValue}>{stats.server.platform}</span>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Base de Datos</div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Tamaño</span>
                <span style={styles.statValue}>{stats.database.sizeFormatted}</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Última modificación</span>
                <span style={styles.statValue}>
                  {stats.database.lastModified ? formatDate(stats.database.lastModified) : '-'}
                </span>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Backups</div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Cantidad</span>
                <span style={styles.statValue}>{stats.backups.count}</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Espacio total</span>
                <span style={styles.statValue}>{stats.backups.totalSizeFormatted}</span>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Registros en Base de Datos</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
              {Object.entries(stats.records).map(([key, value]) => (
                <div key={key} style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#722F37' }}>{value}</div>
                  <div style={{ fontSize: '12px', color: '#666', textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'backups' && (
        <div style={styles.card}>
          <div style={styles.header}>
            <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>Copias de Seguridad</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={{ ...styles.button, ...styles.successButton }}
                onClick={handleCreateBackup}
                disabled={creating}
              >
                {creating ? 'Creando...' : '+ Crear Backup'}
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={handleExportFull}
              >
                Exportar Todo (JSON)
              </button>
            </div>
          </div>

          <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
            Los backups automáticos se crean cada 6 horas. Se mantienen los últimos 10 backups automáticos.
          </p>

          {backups.length === 0 ? (
            <div style={styles.emptyState}>No hay backups disponibles</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Archivo</th>
                    <th style={styles.th}>Tamaño</th>
                    <th style={styles.th}>Fecha</th>
                    <th style={styles.th}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup, idx) => (
                    <tr key={idx}>
                      <td style={styles.td}>
                        <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                          {backup.filename}
                        </span>
                        {backup.filename.includes('_auto_') && (
                          <span style={{ ...styles.badge, background: '#e7f5ff', color: '#1971c2', marginLeft: '10px' }}>
                            Auto
                          </span>
                        )}
                        {backup.filename.includes('_manual_') && (
                          <span style={{ ...styles.badge, background: '#fff3bf', color: '#e67700', marginLeft: '10px' }}>
                            Manual
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{formatBytes(backup.size)}</td>
                      <td style={styles.td}>{formatDate(backup.created_at)}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            style={{ ...styles.button, ...styles.primaryButton, padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleDownloadBackup(backup.filename)}
                          >
                            Descargar
                          </button>
                          <button
                            style={{ ...styles.button, ...styles.successButton, padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleRestoreBackup(backup.filename)}
                          >
                            Restaurar
                          </button>
                          <button
                            style={{ ...styles.button, ...styles.dangerButton, padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleDeleteBackup(backup.filename)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={styles.card}>
          <div style={styles.header}>
            <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>Logs del Sistema</h3>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={fetchData}
            >
              Actualizar
            </button>
          </div>

          {logs.length === 0 ? (
            <div style={styles.emptyState}>No hay logs disponibles</div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {logs.map((log, idx) => (
                <div
                  key={idx}
                  style={{
                    ...styles.logEntry,
                    ...(log.level === 'info' ? styles.logInfo : {}),
                    ...(log.level === 'warn' ? styles.logWarn : {}),
                    ...(log.level === 'error' ? styles.logError : {})
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontWeight: '600', textTransform: 'uppercase' }}>{log.level}</span>
                    <span style={{ color: '#888' }}>{formatDate(log.timestamp)}</span>
                  </div>
                  <div>{log.message}</div>
                  {log.details && (
                    <div style={{ marginTop: '5px', fontSize: '11px', color: '#666' }}>
                      {JSON.stringify(log.details)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'database' && (
        <>
          <div style={styles.card}>
            <div style={styles.header}>
              <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>Visor de Base de Datos</h3>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={fetchDatabase}
              >
                Actualizar
              </button>
            </div>

            <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
              Vista de solo lectura de la base de datos. Las contraseñas están ocultas.
            </p>

            {database ? (
              <pre style={styles.codeBlock}>
                {JSON.stringify(database, null, 2)}
              </pre>
            ) : (
              <div style={styles.emptyState}>Cargando datos...</div>
            )}
          </div>

          {/* Zona de Peligro */}
          <div style={{
            ...styles.card,
            marginTop: '25px',
            border: '2px solid #dc3545',
            background: '#fff5f5'
          }}>
            <h3 style={{ ...styles.cardTitle, color: '#dc3545' }}>
              ⚠️ Zona de Peligro
            </h3>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              padding: '15px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #f0f0f0'
            }}>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                  Reiniciar datos de encuesta activa
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  Elimina todas las respuestas, notificaciones y resetea los tokens de empleados.
                  Se crea un backup automático antes de eliminar.
                </div>
              </div>
              <button
                style={{
                  ...styles.button,
                  ...styles.dangerButton,
                  width: '100%',
                  opacity: resetting ? 0.7 : 1
                }}
                onClick={handleResetSurvey}
                disabled={resetting}
              >
                {resetting ? 'Reiniciando...' : 'Reiniciar Encuesta'}
              </button>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

export default AdminSystem;
